import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type {
  BatchScanRequest,
  BatchScanResponse,
  InventoryExportResponse,
  ScanTicketPayload,
  TicketValidationResponse,
} from '@seatix/check-in-domain';
import { SeatCategoryCacheService } from '@seatix/check-in-data-access-offline-sync';
import { injectScanApiUrl } from '@seatix/shared-config';
import { from, map, Observable, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ScannerApiService {
  readonly #http = inject(HttpClient);
  readonly #apiUrl = injectScanApiUrl();
  readonly #seatCategoryCache = inject(SeatCategoryCacheService);

  validateTicket(
    payload: ScanTicketPayload,
  ): Observable<TicketValidationResponse> {
    return this.#http
      .post<{
        data: TicketValidationResponse;
      }>(`${this.#apiUrl}/api/v1/scan`, {
        ...payload,
        event_id: payload.event_id.toString(),
      })
      .pipe(
        map((response) => response.data),
        switchMap((data) =>
          this.#enrichWithSeatCategory(data, payload.event_id),
        ),
      );
  }

  async #enrichWithSeatCategory(
    response: TicketValidationResponse,
    eventId: number,
  ): Promise<TicketValidationResponse> {
    const ticket = response.ticket;

    if (ticket?.seat?.seat_category) {
      return response;
    }

    const seatCategoryId = (ticket as { seat_category_id?: number })
      ?.seat_category_id;
    if (!seatCategoryId || !ticket?.seat) {
      return response;
    }

    const cachedCategory = await this.#seatCategoryCache.getSeatCategory(
      eventId,
      seatCategoryId,
    );

    if (cachedCategory) {
      return {
        ...response,
        ticket: {
          ...ticket,
          seat: {
            ...ticket.seat,
            seat_category: {
              id: cachedCategory.id,
              name: cachedCategory.name,
              color: cachedCategory.color,
            },
          },
        },
      };
    }

    return response;
  }

  downloadInventory(
    eventId: number,
    from?: number,
  ): Observable<InventoryExportResponse> {
    let params = new HttpParams({
      fromObject: {
        format: 'grouped',
      },
    });

    if (from !== undefined) {
      params = params.set('from', from);
    }

    return this.#http.post<InventoryExportResponse>(
      `${this.#apiUrl}/api/v1/events/${eventId}/tickets/export`,
      {},
      { params },
    );
  }

  syncOfflineScans(
    eventId: number,
    payload: BatchScanRequest,
  ): Observable<BatchScanResponse> {
    return this.#http
      .post<{
        data: BatchScanResponse;
      }>(`${this.#apiUrl}/api/v1/events/${eventId}/scans/batch`, payload)
      .pipe(map((response) => response.data));
  }
}
